import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  CalculatorIcon,
  ChartBarSquareIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import api, { shieldFetch } from '../services/api';
import { maskSensitiveValue, usePrivacy } from '../privacy/PrivacyContext';
import { roundPrivacyNumber } from '../privacy/privacyDisplay';
import {
  MAINTENANCE_SCOPES,
  calculateVehicleSimulation,
} from '../simulation/costCalculator';

const apiUrl = process.env.REACT_APP_URL_LOCAL || 'https://192.168.0.17:1234';

const SimulationPage = () => {
  const navigate = useNavigate();
  const { isPrivacyMode, isShieldModeLevel2 } = usePrivacy();
  const [user, setUser] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [kmInput, setKmInput] = useState('1000');
  const [maintenanceScope, setMaintenanceScope] = useState(MAINTENANCE_SCOPES.ACTIVE);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    let ignore = false;
    const loadUser = async () => {
      try {
        const response = await api.get('/users/me');
        if (!ignore) setUser(response.data);
      } catch (requestError) {
        if (!ignore) navigate('/login');
      }
    };
    loadUser();
    return () => { ignore = true; };
  }, [navigate]);

  useEffect(() => {
    if (!user) return undefined;
    if (Number(user.Droits?.VehicleComparison || 0) <= 0) {
      setLoading(false);
      return undefined;
    }
    let ignore = false;
    const loadVehicles = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await shieldFetch(`${apiUrl}/api/vehicule/${user.UtilisateurID}/cost/simulation/rolling`);
        const payload = await response.json();
        if (!response.ok) throw new Error(payload?.error || 'Impossible de charger les données de simulation.');
        if (!ignore) setVehicles(Array.isArray(payload) ? payload : []);
      } catch (requestError) {
        if (!ignore) setError(requestError.message || 'Impossible de charger les données de simulation.');
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    loadVehicles();
    return () => { ignore = true; };
  }, [user]);

  const simulatedDistance = Number(kmInput);
  const results = useMemo(() => {
    if (!hasCalculated || !Number.isFinite(simulatedDistance) || simulatedDistance <= 0) return [];
    return vehicles.map((vehicle) => calculateVehicleSimulation({
      vehicle,
      distanceKm: simulatedDistance,
      maintenanceScope,
    }));
  }, [hasCalculated, maintenanceScope, simulatedDistance, vehicles]);

  const completeResultCount = results.filter((result) => result.total !== null).length;

  const displayNumber = (value, digits = 2, kind = 'price') => {
    if (value === null || value === undefined || !Number.isFinite(Number(value))) return 'Données insuffisantes';
    const roundedValue = isShieldModeLevel2 ? roundPrivacyNumber(value, kind) : Number(value);
    const formatted = new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(roundedValue);
    return isPrivacyMode && !isShieldModeLevel2 ? maskSensitiveValue(formatted) : formatted;
  };

  const submitSimulation = (event) => {
    event.preventDefault();
    if (!Number.isFinite(simulatedDistance) || simulatedDistance <= 0) {
      setFormError('Saisissez une distance supérieure à zéro.');
      setHasCalculated(false);
      return;
    }
    setFormError('');
    setHasCalculated(true);
  };

  if (user && Number(user.Droits?.VehicleComparison || 0) <= 0) {
    return (
      <section className="mx-auto max-w-3xl rounded-xl border border-amber-500/30 bg-slate-950/70 p-8 text-center text-slate-200">
        <h1 className="text-2xl font-black text-white">Comparaison de véhicules indisponible</h1>
        <p className="mt-2 text-sm text-slate-400">Cette fonctionnalité est incluse dans les offres Premium et Professionnel.</p>
        <button type="button" onClick={() => navigate('/abonnement')} className="mt-5 rounded-lg bg-sky-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-sky-500">Voir les offres</button>
      </section>
    );
  }

  return (
    <main className="mx-auto max-w-7xl space-y-6 text-slate-100">
      <header>
        <p className="text-xs font-black uppercase tracking-[0.18em] text-sky-300">Comparaison des véhicules</p>
        <h1 className="mt-2 text-3xl font-black text-white">Simulation des coûts de roulage</h1>
        <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-400">Projetez les dépenses de carburant et d’entretien à partir des données réellement enregistrées pour chaque véhicule.</p>
      </header>

      {error && <p className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">{error}</p>}

      <form onSubmit={submitSimulation} className="rounded-xl border border-sky-500/20 bg-slate-950/70 p-5 shadow-xl shadow-sky-950/20 sm:p-6">
        <div className="grid gap-6 lg:grid-cols-[minmax(220px,0.45fr)_minmax(0,1fr)_auto] lg:items-end">
          <label className="text-sm font-bold text-slate-200">
            Distance à simuler
            <span className="relative mt-2 block">
              <input type="number" min="1" step="1" value={kmInput} onChange={(event) => { setKmInput(event.target.value); setFormError(''); }} className="w-full rounded-lg border border-sky-500/25 bg-slate-900 px-3 py-3 pr-12 text-lg font-black text-white outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-500/20" aria-describedby="simulation-distance-help" />
              <span className="pointer-events-none absolute right-3 top-3.5 text-sm font-bold text-slate-500">km</span>
            </span>
            <span id="simulation-distance-help" className="mt-2 block text-xs font-normal text-slate-500">La même distance est appliquée à tous les véhicules.</span>
          </label>

          <fieldset>
            <legend className="text-sm font-bold text-slate-200">Périmètre des entretiens réalisés</legend>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              {[
                [MAINTENANCE_SCOPES.ACTIVE, 'Entretiens actifs', 'Archives exclues · premier relevé comme départ'],
                [MAINTENANCE_SCOPES.FULL, 'Historique complet', 'Archives incluses · borne kilométrique la plus ancienne'],
              ].map(([value, label, description]) => (
                <button key={value} type="button" onClick={() => setMaintenanceScope(value)} aria-pressed={maintenanceScope === value} className={`rounded-lg border p-3 text-left transition ${maintenanceScope === value ? 'border-sky-400 bg-sky-500/10 ring-1 ring-sky-400/20' : 'border-white/10 bg-slate-900/60 hover:border-sky-500/30'}`}>
                  <span className="flex items-center gap-2 text-sm font-bold text-white">{maintenanceScope === value && <CheckCircleIcon className="size-4 text-sky-300" />}{label}</span>
                  <span className="mt-1 block text-xs leading-5 text-slate-500">{description}</span>
                </button>
              ))}
            </div>
          </fieldset>

          <button type="submit" disabled={loading} className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-sky-600 px-5 text-sm font-black text-white transition hover:bg-sky-500 disabled:cursor-not-allowed disabled:opacity-50">
            <CalculatorIcon className="size-5" /> Calculer
          </button>
        </div>
        {formError && <p className="mt-4 text-sm font-semibold text-rose-300">{formError}</p>}
      </form>

      {loading ? (
        <section className="rounded-xl border border-white/10 bg-slate-950/60 p-10 text-center text-slate-400"><ArrowPathIcon className="mx-auto size-7 animate-spin text-sky-300" /><p className="mt-3 text-sm">Chargement des données de roulage…</p></section>
      ) : hasCalculated ? (
        <>
          <section className="grid gap-4 sm:grid-cols-3">
            <article className="rounded-xl border border-sky-500/20 bg-slate-950/70 p-5"><ChartBarSquareIcon className="size-6 text-sky-300" /><p className="mt-4 text-2xl font-black text-white">{vehicles.length}</p><p className="text-sm text-slate-500">véhicule{vehicles.length > 1 ? 's' : ''} comparé{vehicles.length > 1 ? 's' : ''}</p></article>
            <article className="rounded-xl border border-emerald-500/20 bg-slate-950/70 p-5"><CheckCircleIcon className="size-6 text-emerald-300" /><p className="mt-4 text-2xl font-black text-white">{completeResultCount}</p><p className="text-sm text-slate-500">estimation{completeResultCount > 1 ? 's' : ''} complète{completeResultCount > 1 ? 's' : ''}</p></article>
            <article className="rounded-xl border border-fuchsia-500/20 bg-slate-950/70 p-5"><WrenchScrewdriverIcon className="size-6 text-fuchsia-300" /><p className="mt-4 text-2xl font-black text-white">{maintenanceScope === MAINTENANCE_SCOPES.FULL ? 'Complet' : 'Actif'}</p><p className="text-sm text-slate-500">périmètre d’entretien sélectionné</p></article>
          </section>

          <section className="overflow-hidden rounded-xl border border-sky-500/20 bg-slate-950/70 shadow-xl shadow-sky-950/20">
            <div className="border-b border-white/10 px-5 py-4 sm:px-6">
              <h2 className="text-lg font-black text-white">Résultats pour {displayNumber(simulatedDistance, 0, 'kilometers')} km</h2>
              <p className="mt-1 text-xs text-slate-500">Le coût carburant utilise la médiane des derniers intervalles valides. Aucun coefficient d’entretien fixe n’est appliqué.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-[1180px] w-full divide-y divide-white/10 text-sm">
                <thead className="bg-slate-900/80 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr><th className="px-5 py-4">Véhicule</th><th className="px-4 py-4">Source entretien</th><th className="px-4 py-4 text-right">Carburant/km</th><th className="px-4 py-4 text-right">Entretien/km</th><th className="px-4 py-4 text-right">Carburant estimé</th><th className="px-4 py-4 text-right">Entretien estimé</th><th className="px-5 py-4 text-right">Coût total</th></tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {results.map((result) => {
                    const maintenanceReady = result.maintenance.status === 'OK';
                    return (
                      <tr key={result.vehicle.VehiculeID} className="text-slate-300 hover:bg-white/[0.025]">
                        <td className="px-5 py-4"><span className="block font-black text-white">{result.vehicle.Nom}</span><span className="text-xs text-slate-500">{result.vehicle.TypeNom || 'Type non renseigné'}</span></td>
                        <td className="max-w-72 px-4 py-4">{maintenanceReady ? <span><span className="block font-semibold text-emerald-200">{result.maintenance.maintenanceCount} entretien{result.maintenance.maintenanceCount > 1 ? 's' : ''} chiffré{result.maintenance.maintenanceCount > 1 ? 's' : ''}</span><span className="text-xs text-slate-500">{displayNumber(result.maintenance.distanceKm, 0, 'kilometers')} km observés · {displayNumber(result.maintenance.observedCost)} € enregistrés</span><span className="mt-1 block text-[11px] text-slate-600">Départ : {result.maintenance.startSource === 'PLUS_ANCIEN_ENTRETIEN' ? 'plus ancien entretien' : 'premier relevé'}</span></span> : <span className="inline-flex items-start gap-2 text-xs leading-5 text-amber-200"><ExclamationTriangleIcon className="mt-0.5 size-4 shrink-0" />{result.maintenance.reason}</span>}</td>
                        <td className="px-4 py-4 text-right font-semibold">{result.fuel.status === 'OK' ? `${displayNumber(result.fuel.costPerKm, 3)} €` : <span className="text-xs text-amber-200">Données insuffisantes</span>}</td>
                        <td className="px-4 py-4 text-right font-semibold">{maintenanceReady ? `${displayNumber(result.maintenance.costPerKm, 3)} €` : <span className="text-xs text-amber-200">Données insuffisantes</span>}</td>
                        <td className="px-4 py-4 text-right font-semibold">{result.fuelTotal !== null ? `${displayNumber(result.fuelTotal)} €` : '—'}</td>
                        <td className="px-4 py-4 text-right font-semibold">{result.maintenanceTotal !== null ? `${displayNumber(result.maintenanceTotal)} €` : '—'}</td>
                        <td className="px-5 py-4 text-right">{result.total !== null ? <><span className="block text-lg font-black text-sky-200">{displayNumber(result.total)} €</span><span className="text-xs text-slate-500">{displayNumber(result.totalPerKm, 3)} €/km</span></> : <span className="text-xs font-bold text-amber-200">Données insuffisantes</span>}</td>
                      </tr>
                    );
                  })}
                  {results.length === 0 && <tr><td colSpan="7" className="px-5 py-10 text-center text-slate-500">Aucun véhicule actif accessible.</td></tr>}
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : (
        <section className="rounded-xl border border-dashed border-sky-500/25 bg-slate-950/50 p-10 text-center"><CalculatorIcon className="mx-auto size-9 text-sky-300" /><h2 className="mt-4 text-lg font-black text-white">Préparez votre comparaison</h2><p className="mt-2 text-sm text-slate-500">Choisissez une distance et le périmètre des entretiens, puis lancez le calcul.</p></section>
      )}
    </main>
  );
};

export default SimulationPage;
